import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Input } from '../../../components/ui';
import { Trophy, Calendar, RefreshCw, DollarSign, Store, ChevronDown, ChevronRight, Gift } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';

// Tipos - respuesta de /premios/por-local
interface JugadaGanadora {
  jugada_id: string;
  ticket_id: string;
  ticket_number: string | null;
  vendedor_id: string;
  num1: string;
  num2: string | null;
  concepto: string;
  monto_jugado: string;
  multiplicador: number;
  pago: string;
  regla_origen: string;
}

interface PremioLocal {
  local_id: string;
  local_nombre: string;
  total_local: string;
  jugadas_ganadoras: JugadaGanadora[];
  warnings: string[];
}

interface PremioSorteo {
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  ganador_num1: string;
  ganador_num2: string | null;
  ganador_num3: string | null;
  ganador_paga_reventado: boolean | null;
  ganador_verificado: boolean;
  total_sorteo: string;
  locales: PremioLocal[];
}

interface PremiosPorLocalResponse {
  fecha: string;
  total_global: string;
  sorteos: PremioSorteo[];
  sorteos_sin_ganador: string[];
}

// Config
const TIPOS_CONFIG: Record<string, { label: string; color: string }> = {
  NORMAL: { label: 'Normal', color: 'bg-blue-600' },
  REVENTADO: { label: 'Reventado', color: 'bg-orange-600' },
  MEGA: { label: 'Mega', color: 'bg-red-600' },
  MONAZOS: { label: 'Monazos', color: 'bg-purple-600' },
  FECHAS: { label: 'Fechas', color: 'bg-green-600' },
  PARLEY: { label: 'Parley', color: 'bg-pink-600' },
};

const CONCEPTO_LABELS: Record<string, string> = {
  DIRECTO: 'Directo',
  REVENTADO: 'Reventado',
  MEGA_REVENTADO: 'Mega Reventado',
  EN_ORDEN: 'En Orden',
  DESORDEN: 'Desorden',
  PRIMEROS_2: 'Primeros 2',
  ULTIMOS_2: 'Ultimos 2',
};

const REGLA_BADGE: Record<string, { label: string; variant: 'success' | 'neutral' | 'warning' | 'danger' }> = {
  CONGELADA: { label: 'Congelada', variant: 'success' },
  CONGELADA_GLOBAL: { label: 'Congelada', variant: 'success' },
  GLOBAL: { label: 'Global', variant: 'neutral' },
  LOCAL: { label: 'Local', variant: 'warning' },
  SIN_REGLA: { label: 'Sin Regla', variant: 'danger' },
};

const formatMonto = (val: string) => {
  const n = Number(val);
  if (isNaN(n)) return val;
  return n.toLocaleString('es-CR', { minimumFractionDigits: 0 });
};

export const NumerosGanadoresPage: React.FC = () => {
  const [fechaFiltro, setFechaFiltro] = useState(fechaLocalYMD);
  const [data, setData] = useState<PremiosPorLocalResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSorteos, setExpandedSorteos] = useState<Set<string>>(new Set());
  const [expandedLocales, setExpandedLocales] = useState<Set<string>>(new Set());

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchAPI<PremiosPorLocalResponse>(
        `/premios/por-local?fecha=${fechaFiltro}&incluir_no_verificados=true`
      );
      setData(result);
      setExpandedSorteos(new Set(result.sorteos.map(s => s.sorteo_id)));
      setExpandedLocales(new Set());
    } catch (e: any) {
      setError(e.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [fechaFiltro]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const toggleSorteo = (id: string) => {
    setExpandedSorteos(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleLocal = (key: string) => {
    setExpandedLocales(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const sorteosConPago = data ? data.sorteos.filter(s => Number(s.total_sorteo) > 0).length : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ganadores</h1>
          <p className="text-gray-500">Resumen de premios por sorteo y local</p>
        </div>
        <button
          onClick={cargarDatos}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
          title="Refrescar"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Filtro fecha */}
      <Card>
        <div className="flex-1 max-w-xs">
          <Input
            type="date"
            value={fechaFiltro}
            onChange={(e) => setFechaFiltro(e.target.value)}
            leftIcon={<Calendar size={18} />}
          />
        </div>
      </Card>

      {/* Stats */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Premios</p>
                <p className="text-2xl font-bold text-blue-600">{formatMonto(data.total_global)}</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Trophy size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Sorteos con Premios</p>
                <p className="text-2xl font-bold text-green-600">{sorteosConPago}</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Gift size={20} className="text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Sin Ganador</p>
                <p className="text-2xl font-bold text-yellow-600">{data.sorteos_sin_ganador.length}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-8 text-gray-500">Cargando premios...</div>
      )}

      {/* Sin datos */}
      {!loading && data && data.sorteos.length === 0 && (
        <Card>
          <div className="text-center py-8 text-gray-500">
            No hay premios calculados para esta fecha
          </div>
        </Card>
      )}

      {/* Lista por sorteo */}
      {!loading && data && data.sorteos.map(sorteo => {
        const isExpanded = expandedSorteos.has(sorteo.sorteo_id);
        const tipoCfg = TIPOS_CONFIG[sorteo.tipo_id] || { label: sorteo.tipo_id, color: 'bg-gray-600' };

        return (
          <Card key={sorteo.sorteo_id}>
            {/* Header sorteo */}
            <button
              onClick={() => toggleSorteo(sorteo.sorteo_id)}
              className="w-full flex items-center justify-between py-1"
            >
              <div className="flex items-center gap-3">
                {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <span className={`px-2 py-0.5 rounded text-xs text-white font-medium ${tipoCfg.color}`}>
                  {tipoCfg.label}
                </span>
                <span className="font-semibold text-gray-900">{sorteo.sorteo_nombre}</span>
                <span className="text-gray-500 text-sm font-mono">#{sorteo.ganador_num1}</span>
                {!sorteo.ganador_verificado && (
                  <Badge variant="warning">No verificado</Badge>
                )}
              </div>
              <span className="text-lg font-bold text-blue-600">{formatMonto(sorteo.total_sorteo)}</span>
            </button>

            {/* Detalle por local */}
            {isExpanded && (
              <div className="mt-4 space-y-3 border-t pt-4">
                {sorteo.locales.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-2">Sin jugadas ganadoras en este sorteo</p>
                )}
                {sorteo.locales.map(local => {
                  const localKey = `${sorteo.sorteo_id}:${local.local_id}`;
                  const isLocalExpanded = expandedLocales.has(localKey);

                  return (
                    <div key={local.local_id} className="border rounded-lg">
                      {/* Header local */}
                      <button
                        onClick={() => toggleLocal(localKey)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-2">
                          {isLocalExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          <Store size={16} className="text-gray-400" />
                          <span className="font-medium text-gray-800">{local.local_nombre}</span>
                          <span className="text-xs text-gray-400">({local.jugadas_ganadoras.length} jugadas)</span>
                        </div>
                        <span className="font-bold text-gray-900">{formatMonto(local.total_local)}</span>
                      </button>

                      {/* Warnings */}
                      {local.warnings.length > 0 && (
                        <div className="mx-4 mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-700 text-xs">
                          {local.warnings.join(' | ')}
                        </div>
                      )}

                      {/* Tabla de jugadas ganadoras */}
                      {isLocalExpanded && (
                        <div className="px-4 pb-3">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b">
                                  <th className="py-2 pr-2">Ticket</th>
                                  <th className="py-2 pr-2">Numero</th>
                                  <th className="py-2 pr-2">Concepto</th>
                                  <th className="py-2 pr-2 text-right">Jugado</th>
                                  <th className="py-2 pr-2 text-right">Multi.</th>
                                  <th className="py-2 pr-2 text-right">Pago</th>
                                  <th className="py-2">Regla</th>
                                </tr>
                              </thead>
                              <tbody>
                                {local.jugadas_ganadoras.map((jg, idx) => {
                                  const badge = REGLA_BADGE[jg.regla_origen] || REGLA_BADGE.SIN_REGLA;
                                  return (
                                    <tr
                                      key={idx}
                                      className={`border-b last:border-0 ${jg.regla_origen === 'SIN_REGLA' ? 'bg-red-50' : ''}`}
                                    >
                                      <td className="py-2 pr-2 font-mono text-xs">{jg.ticket_number || jg.ticket_id.slice(0, 10)}</td>
                                      <td className="py-2 pr-2 font-mono">
                                        #{jg.num1}{jg.num2 ? ` / ${jg.num2}` : ''}
                                      </td>
                                      <td className="py-2 pr-2">{CONCEPTO_LABELS[jg.concepto] || jg.concepto}</td>
                                      <td className="py-2 pr-2 text-right">{formatMonto(jg.monto_jugado)}</td>
                                      <td className="py-2 pr-2 text-right font-medium">x{jg.multiplicador}</td>
                                      <td className="py-2 pr-2 text-right font-bold">{formatMonto(jg.pago)}</td>
                                      <td className="py-2">
                                        <Badge variant={badge.variant}>{badge.label}</Badge>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default NumerosGanadoresPage;
